import { formatMicroCreditsAsCredits } from '@this+that/models';

/** pct >= this is "healthy"; below it (and > 0 balance) is "low". Matches the design handoff. */
const LOW_BALANCE_PCT_THRESHOLD = 0.1;

export enum CreditsCardVariant {
  /** hasWallet: false — no wallet provisioned yet, not an error state. */
  NO_WALLET = 'no_wallet',
  HEALTHY = 'healthy',
  LOW = 'low',
  EMPTY = 'empty',
}

export interface CreditsBalanceInput {
  hasWallet: boolean;
  planAllowanceMicros: number;
  planRemainingMicros: number;
  purchasedMicros: number;
  totalSpendableMicros: number;
}

export interface CreditsBalanceDerived {
  balanceMicros: number;
  totalMicros: number;
  usedMicros: number;
  /** 0..1, clamped at 0 when totalMicros is 0 to avoid NaN/Infinity. */
  pct: number;
  variant: CreditsCardVariant;
}

function resolveVariant(
  hasWallet: boolean,
  balanceMicros: number,
  pct: number,
): CreditsCardVariant {
  if (!hasWallet) {
    return CreditsCardVariant.NO_WALLET;
  }
  if (balanceMicros <= 0) {
    return CreditsCardVariant.EMPTY;
  }
  if (pct < LOW_BALANCE_PCT_THRESHOLD) {
    return CreditsCardVariant.LOW;
  }
  return CreditsCardVariant.HEALTHY;
}

/**
 * total = plan allowance + purchased (purchased used as-is, even negative);
 * used = allowance - plan remaining; balance = totalSpendableMicros. This
 * makes balance = total - used hold, per the product override on the design
 * handoff (the allowance resets monthly instead of a one-time 3000 grant).
 */
export function deriveCreditsBalance(
  input: CreditsBalanceInput,
): CreditsBalanceDerived {
  const totalMicros = input.planAllowanceMicros + input.purchasedMicros;
  const usedMicros = input.planAllowanceMicros - input.planRemainingMicros;
  const balanceMicros = input.totalSpendableMicros;
  const pct = totalMicros > 0 ? balanceMicros / totalMicros : 0;

  return {
    balanceMicros,
    totalMicros,
    usedMicros,
    pct,
    variant: resolveVariant(input.hasWallet, balanceMicros, pct),
  };
}

/** Whole-credit, locale-aware formatting (pt-BR: "2.140", en-US: "2,140"). */
export function formatCreditsAmount(micros: number, locale: string): string {
  const credits = Math.round(formatMicroCreditsAsCredits(micros));
  return new Intl.NumberFormat(locale).format(credits);
}

const RENEWAL_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
};

/** dd/mm (or locale-equivalent short day/month), for "renews on {date}". */
export function formatCreditsRenewalDate(
  timestampMs: number,
  locale: string,
): string {
  return new Intl.DateTimeFormat(locale, RENEWAL_DATE_FORMAT_OPTIONS).format(
    new Date(timestampMs),
  );
}

/** Whole-percent, clamped to [0, 100] for display (pct can exceed 1 only via stale/bad data). */
export function toWholePercent(pct: number): number {
  return Math.round(Math.min(Math.max(pct, 0), 1) * 100);
}
